import { useAuth } from '../lib/auth-context';
import { supabase } from '../lib/supabase';
import useSWR from 'swr';

interface Alert {
  readonly alert_id: string;
  readonly alert_type: string;
  readonly title: string;
  readonly severity: string;
  readonly is_read: boolean;
  readonly dispatched_at: string;
}

export function AlertsPage() {
  const { user } = useAuth();
  const { data: alerts, error, mutate: mutateAlerts } = useSWR(
    user ? ['alerts', user.id] : null,
    async () => {
      const { data } = await supabase
        .from('tenant_alerts')
        .select('*')
        .eq('tenant_id', user!.id)
        .order('dispatched_at', { ascending: false })
        .limit(50);
      return (data ?? []) as readonly Alert[];
    },
  );

  const handleMarkRead = async (alertId: string) => {
    await supabase
      .from('tenant_alerts')
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq('alert_id', alertId);
    mutateAlerts();
  };

  if (error) return <div className="text-red-600">Failed to load alerts.</div>;
  if (!alerts) return <div className="text-gray-500">Loading alerts...</div>;

  const unreadCount = alerts.filter((a) => !a.is_read).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Alerts</h2>
        {unreadCount > 0 && (
          <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm">
            {unreadCount} unread
          </span>
        )}
      </div>

      {alerts.length === 0 ? (
        <p className="text-gray-500">No alerts yet. Alerts will appear here when monitoring detects relevant events for your subscription tier.</p>
      ) : (
        <div className="space-y-3">
          {alerts.map((alert) => (
            <div
              key={alert.alert_id}
              className={`bg-white rounded-lg shadow p-4 flex items-start gap-4 ${
                !alert.is_read ? 'border-l-4 border-sentinel-500' : ''
              }`}
            >
              <div
                className={`w-2 h-2 rounded-full mt-2 ${
                  alert.severity === 'critical'
                    ? 'bg-red-500'
                    : alert.severity === 'warning'
                      ? 'bg-yellow-500'
                      : 'bg-blue-500'
                }`}
              />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">{alert.title}</h4>
                  <span className="text-xs text-gray-500">{new Date(alert.dispatched_at).toLocaleString()}</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">{alert.alert_type}</p>
              </div>
              {!alert.is_read && (
                <button
                  onClick={() => handleMarkRead(alert.alert_id)}
                  className="text-sm text-sentinel-700 hover:underline"
                >
                  Mark read
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
